// Saving a profile through the potential-matches gate (BRD 10.1, Spec 2.3 "Given/When/Then: potential matches appear
// before save."): the first Save asks candidate.match with the values on screen; with any match the gate opens and
// nothing is written; the next Save carries the person's decision and the refs of every match shown. A changed value
// closes the gate (the matches were for the old one). A match that appeared since the preview is refused at save
// (`potential_matches`): the grid is refreshed and the refusal is his sentence above it.

import { useEffect, useState } from "react";
import { AppError } from "@/api/runtime";
import type { CandidateCreateInput, CandidateCreateOutput, CreateDecision, HitOut } from "@/api/tools";
import { candidateCreate, candidateMatch } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import type { Entry } from "./PotentialMatches";
import { refusalSentence, ruleCode } from "./refusals";

export interface SaveGate {
  /** The matches shown, or null before the first Save (or after a value changed). */
  hits: HitOut[] | null;
  strong: number;
  distinct: boolean;
  setDistinct: (on: boolean) => void;
  reason: string;
  setReason: (reason: string) => void;
  /** His sentence for the last refusal, if any (entered values kept). */
  refused: string | null;
  /** A system failure (5xx, lost connection): the error strip. */
  failure: AppError | null;
  /** The field the last refusal named. */
  fieldErrors: Record<string, string>;
  busy: boolean;
  /** Save with the gate's decision (Create distinct, a Coordinator's referral, or none). */
  save: () => Promise<void>;
  /** Use existing: links the existing profile to the requisition in context. */
  useExisting: (hit: HitOut) => Promise<void>;
}

export function useSaveGate(opts: {
  entry: Entry;
  requisitionId: string | null;
  mayResolve: boolean;
  /** The create input without the gate's fields. */
  build: () => Omit<CandidateCreateInput, "decision" | "seen_match_refs">;
  onSaved: (out: CandidateCreateOutput) => void;
}): SaveGate {
  const { entry, requisitionId, mayResolve, build, onSaved } = opts;
  const [hits, setHits] = useState<HitOut[] | null>(null);
  const [distinct, setDistinct] = useState(false);
  const [reason, setReason] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const [failure, setFailure] = useState<AppError | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [matching, setMatching] = useState(false);
  const create = useWrite((input: CandidateCreateInput, key) => candidateCreate(input, key), [["candidate-search"], ["candidate-parse"]]);
  const key = JSON.stringify([entry, requisitionId]);
  useEffect(() => { setHits(null); setDistinct(false); }, [key]);

  const match = async () => {
    const out = await candidateMatch({
      display_name: entry.name.trim() || null,
      email: entry.email.trim() || null,
      secondary_email: entry.secondaryEmail.trim() || null,
      phone: entry.phone.trim() || null,
      linkedin_url: entry.linkedinUrl.trim() || null,
      requisition_id: requisitionId,
    });
    return out.hits;
  };

  const fail = async (error: unknown) => {
    if (!(error instanceof AppError)) throw error;
    if (error.status >= 500 || error.code === "NETWORK") {
      setFailure(error);
      return;
    }
    setFieldErrors(error.fieldErrors);
    setRefused(refusalSentence(error));
    if (ruleCode(error) === "potential_matches") setHits(await match().catch(() => hits ?? []));
  };

  const submit = async (decision: CreateDecision | null, shown: HitOut[]) => {
    setRefused(null);
    setFailure(null);
    setFieldErrors({});
    try {
      const out = await create.mutateAsync({ ...build(), decision, seen_match_refs: shown.map((hit) => hit.match_ref) });
      onSaved(out);
    } catch (error) {
      await fail(error);
    }
  };

  const strong = (hits ?? []).filter((hit) => hit.strength === "strong").length;

  const save = async () => {
    if (hits === null) {
      setMatching(true);
      setRefused(null);
      setFailure(null);
      try {
        const found = await match();
        if (found.length > 0) {
          setHits(found);
          return;
        }
        setHits([]);
        await submit(null, []);
      } catch (error) {
        await fail(error);
      } finally {
        setMatching(false);
      }
      return;
    }
    let decision: CreateDecision | null = null;
    if (strong > 0 && !mayResolve) decision = { kind: "refer" };
    else if (distinct) decision = { kind: "create_distinct", reason: reason.trim() };
    await submit(decision, hits);
  };

  const useExisting = async (hit: HitOut) => {
    await submit({ kind: "use_existing", match_ref: hit.match_ref }, hits ?? [hit]);
  };

  return {
    hits, strong, distinct, setDistinct, reason, setReason, refused, failure, fieldErrors,
    busy: matching || create.isPending, save, useExisting,
  };
}

/** Save is blocked while a strong match waits for the person's decision (a Coordinator's Save refers it). */
export function gateBlocked(gate: SaveGate, mayResolve: boolean): boolean {
  if (gate.hits === null || gate.strong === 0 || !mayResolve) return false;
  return !gate.distinct || gate.reason.trim().length < 3;
}
